import { useState, useMemo } from "react";
import { Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Inbox } from "lucide-react";

/**
 * Reusable DataTable with built-in:
 * - Search input (controlled or self-managed)
 * - Filter slots (children-based)
 * - Pagination (10/25/50/100)
 * - Loading + empty states
 * - Sortable columns (optional, client-side)
 *
 * Props:
 * - rows: array of records
 * - columns: [{ key, label, render?(row), sortable?, className?, headerClassName? }]
 * - searchTerm, onSearchChange (controlled). If onSearchChange is omitted, search filters rows client-side using `searchableFields`.
 * - searchableFields: array of keys to search client-side
 * - filters: ReactNode rendered next to search input
 * - actions: ReactNode rendered top-right (e.g. "+ Hinzufügen")
 * - loading: bool
 * - emptyTitle, emptyHint: empty state text
 * - testid: data-testid prefix
 * - rowKey(row): id getter (defaults to row.id)
 * - onRowClick?(row)
 * - rowClassName?(row, idx)
 * - title, subtitle: optional header
 * - defaultPageSize: 10|25|50|100 — default 25
 * - footerHint: small text below pagination
 */
export default function DataTable({
  rows = [],
  columns,
  searchTerm,
  onSearchChange,
  searchableFields = [],
  searchPlaceholder = "Suchen...",
  filters,
  actions,
  loading = false,
  emptyTitle = "Keine Einträge",
  emptyHint = "Keine Daten zum Anzeigen.",
  testid = "data-table",
  rowKey = (r) => r.id,
  onRowClick,
  rowClassName,
  defaultPageSize = 25,
  footerHint,
  hideSearch = false,
}) {
  const [innerSearch, setInnerSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const isControlled = typeof onSearchChange === "function";
  const term = isControlled ? (searchTerm || "") : innerSearch;

  const filtered = useMemo(() => {
    if (!term || isControlled || searchableFields.length === 0) return rows;
    const t = term.toLowerCase();
    return rows.filter((r) =>
      searchableFields.some((f) => {
        let v;
        if (typeof f === "function") {
          try { v = f(r); } catch { v = null; }
        } else if (typeof f === "string" && f.includes(".")) {
          v = f.split(".").reduce((o, k) => (o == null ? o : o[k]), r);
        } else {
          v = r[f];
        }
        return v != null && String(v).toLowerCase().includes(t);
      })
    );
  }, [rows, term, isControlled, searchableFields]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      const an = typeof av === "number" ? av : String(av);
      const bn = typeof bv === "number" ? bv : String(bv);
      if (an < bn) return sortDir === "asc" ? -1 : 1;
      if (an > bn) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const end = Math.min(start + pageSize, total);
  const slice = sorted.slice(start, end);

  function handleSearch(e) {
    const v = e.target.value;
    if (isControlled) onSearchChange(v); else setInnerSearch(v);
    setPage(1);
  }
  function handleSort(col) {
    if (!col.sortable) return;
    if (sortKey === col.key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(col.key); setSortDir("asc"); }
  }

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md" data-testid={testid}>
      {(filters || actions || !hideSearch) && (
        <div className="p-4 flex gap-3 flex-wrap border-b border-zinc-800 items-center">
          {!hideSearch && (
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" />
              <input
                type="text"
                value={term}
                onChange={handleSearch}
                placeholder={searchPlaceholder}
                data-testid={`${testid}-search`}
                className="w-full pl-9 pr-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:ring-2 focus:ring-blue-600 focus:outline-none"
              />
            </div>
          )}
          {filters}
          {actions && <div className="ml-auto flex gap-2">{actions}</div>}
        </div>
      )}

      {loading ? (
        <div className="p-12 text-center text-zinc-500 text-sm" data-testid={`${testid}-loading`}>
          <div className="inline-block w-6 h-6 border-2 border-zinc-700 border-t-blue-500 rounded-full animate-spin mb-3"></div>
          <div>Lade Daten...</div>
        </div>
      ) : total === 0 ? (
        <div className="p-12 text-center" data-testid={`${testid}-empty`}>
          <Inbox className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <div className="text-zinc-300 font-medium">{emptyTitle}</div>
          <div className="text-zinc-500 text-sm mt-1">{emptyHint}</div>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10">
                <tr className="bg-zinc-900 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className={`px-4 py-3 text-left font-semibold ${col.headerClassName || ""} ${col.sortable ? "cursor-pointer select-none hover:text-zinc-200" : ""}`}
                      onClick={() => handleSort(col)}
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {col.sortable && sortKey === col.key && (
                          <span className="text-blue-400">{sortDir === "asc" ? "▲" : "▼"}</span>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {slice.map((row, idx) => (
                  <tr
                    key={rowKey(row) || idx}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    data-testid={`${testid}-row-${rowKey(row) || idx}`}
                    className={`border-b border-zinc-800/60 transition-colors ${
                      idx % 2 === 0 ? "bg-zinc-900/40" : "bg-zinc-900"
                    } hover:bg-zinc-800/40 ${onRowClick ? "cursor-pointer" : ""} ${
                      typeof rowClassName === "function" ? rowClassName(row, idx) || "" : ""
                    }`}
                  >
                    {columns.map((col) => (
                      <td key={col.key} className={`px-4 py-3 ${col.className || ""}`}>
                        {col.render ? col.render(row) : (row[col.key] ?? "—")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-zinc-800 text-xs text-zinc-400 flex-wrap" data-testid={`${testid}-pagination`}>
            <div className="flex items-center gap-2">
              <span>Zeilen pro Seite:</span>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(1); }}
                data-testid={`${testid}-page-size`}
                className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-zinc-100"
              >
                {[10, 25, 50, 100].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {footerHint && <span className="ml-3 text-zinc-500">{footerHint}</span>}
            </div>
            <div className="flex items-center gap-2">
              <span data-testid={`${testid}-range`}>
                {total === 0 ? "0" : `${start + 1}-${end}`} von {total}
              </span>
              <div className="flex gap-1 ml-2">
                <PgBtn disabled={safePage === 1} onClick={() => setPage(1)} data-testid={`${testid}-first`}><ChevronsLeft className="w-3.5 h-3.5"/></PgBtn>
                <PgBtn disabled={safePage === 1} onClick={() => setPage(safePage - 1)} data-testid={`${testid}-prev`}><ChevronLeft className="w-3.5 h-3.5"/></PgBtn>
                <span className="px-3 py-1 text-zinc-100 font-mono">{safePage}/{totalPages}</span>
                <PgBtn disabled={safePage === totalPages} onClick={() => setPage(safePage + 1)} data-testid={`${testid}-next`}><ChevronRight className="w-3.5 h-3.5"/></PgBtn>
                <PgBtn disabled={safePage === totalPages} onClick={() => setPage(totalPages)} data-testid={`${testid}-last`}><ChevronsRight className="w-3.5 h-3.5"/></PgBtn>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function PgBtn({ children, disabled, onClick, ...rest }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      {...rest}
      className="p-1.5 bg-zinc-950 border border-zinc-800 rounded text-zinc-300 hover:text-zinc-100 hover:border-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );
}
